/**
 * SettingsPage — page Réglages.
 *
 * Hôte unique pour le catalogue paramétrable atelier (formats, encadrements,
 * supports, intentions émotionnelles, publics cibles) + config livrables
 * digitaux (S2.5e — formats actifs, DPI, qualité JPG, document
 * accompagnant FR/EN, batch régénération via queue S5b). Tabs internes
 * par section. Les templates mockup ont leur propre page sidebar
 * (`/mockups`) car ce sont des actifs créatifs, pas des listes de valeurs.
 */

import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router'

import Page from '@/components/layout/Page'
import Tabs from '@/components/ui/Tabs'

import DeliverablesTab from './settings/DeliverablesTab'
import DiffusionTab from './settings/DiffusionTab'
import EmotionalIntentsTab from './settings/EmotionalIntentsTab'
import FormatsTab from './settings/FormatsTab'
import FramingsTab from './settings/FramingsTab'
import PlatformRulesTab from './settings/PlatformRulesTab'
import PricingTab from './settings/PricingTab'
import PrintableSpecsTab from './settings/PrintableSpecsTab'
import SupportsTab from './settings/SupportsTab'
import TargetAudiencesTab from './settings/TargetAudiencesTab'

import './SettingsPage.css'

type SettingsTab =
  | 'formats'
  | 'framings'
  | 'supports'
  | 'emotional-intents'
  | 'target-audiences'
  | 'deliverables'
  | 'platform-rules'
  | 'printable-specs'
  | 'pricing'
  | 'connections'

const TAB_ITEMS: { value: SettingsTab; label: string }[] = [
  { value: 'formats', label: 'Formats' },
  { value: 'framings', label: 'Encadrements' },
  { value: 'supports', label: 'Supports' },
  { value: 'emotional-intents', label: 'Intentions émotionnelles' },
  { value: 'target-audiences', label: 'Publics cibles' },
  { value: 'deliverables', label: 'Livrables digitaux' },
  { value: 'platform-rules', label: "Règles d'éligibilité" },
  { value: 'printable-specs', label: 'Catalogue POD' },
  { value: 'pricing', label: 'Tarification' },
  { value: 'connections', label: 'Diffusion' },
]

const DEFAULT_TAB: SettingsTab = 'formats'
const TAB_VALUES = new Set<string>(TAB_ITEMS.map((item) => item.value))

// Refonte tarifs 2026-05-15 : slugs legacy redirigés vers `pricing`. Tout
// bookmark utilisateur ou lien externe sur l'ancien slug migre
// automatiquement (URL réécrite via setParams replace).
const LEGACY_TAB_REDIRECTS: Record<string, SettingsTab> = {
  'atelier-pricing': 'pricing',
  'pod-pricing': 'pricing',
}

function isSettingsTab(value: string | null): value is SettingsTab {
  return value !== null && TAB_VALUES.has(value)
}

export default function SettingsPage() {
  // `tab` est posé en query param pour permettre des liens directs
  // (`/settings?tab=connections`) — utile pour le callback Etsy OAuth qui
  // redirige sur `/settings?tab=connections&etsy=ok`. Source de vérité = URL.
  const [params, setParams] = useSearchParams()
  const rawTab = params.get('tab')

  // Refonte tarifs 2026-05-15 : redirect legacy slugs `atelier-pricing` /
  // `pod-pricing` → `pricing`. Vrai redirect (URL réécrite via replace)
  // pour que le bookmark migre — pas juste un rendu sous l'ancien slug.
  useEffect(() => {
    if (rawTab && rawTab in LEGACY_TAB_REDIRECTS) {
      const updated = new URLSearchParams(params)
      updated.set('tab', LEGACY_TAB_REDIRECTS[rawTab])
      setParams(updated, { replace: true })
    }
  }, [rawTab, params, setParams])

  const tab: SettingsTab = isSettingsTab(rawTab)
    ? rawTab
    : rawTab && rawTab in LEGACY_TAB_REDIRECTS
      ? LEGACY_TAB_REDIRECTS[rawTab]
      : DEFAULT_TAB

  const setTab = useCallback(
    (next: SettingsTab) => {
      const updated = new URLSearchParams(params)
      updated.set('tab', next)
      setParams(updated, { replace: true })
    },
    [params, setParams],
  )

  return (
    <Page width="wide">
      <Page.Header
        eyebrow="Atelier"
        title="Réglages"
        subtitle="Catalogue paramétrable + livrables digitaux utilisés par les variantes d'œuvres."
      />

      <Tabs<SettingsTab>
        value={tab}
        onChange={setTab}
        items={TAB_ITEMS}
        ariaLabel="Sections du catalogue"
        className="settings-tabs"
      />

      <div className="settings-tab-panel">
        {tab === 'formats' && <FormatsTab />}
        {tab === 'framings' && <FramingsTab />}
        {tab === 'supports' && <SupportsTab />}
        {tab === 'emotional-intents' && <EmotionalIntentsTab />}
        {tab === 'target-audiences' && <TargetAudiencesTab />}
        {tab === 'deliverables' && <DeliverablesTab />}
        {tab === 'platform-rules' && <PlatformRulesTab />}
        {tab === 'printable-specs' && <PrintableSpecsTab />}
        {tab === 'pricing' && <PricingTab />}
        {tab === 'connections' && <DiffusionTab />}
      </div>
    </Page>
  )
}
